import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, LockKeyhole, Mail, UserRound } from "lucide-react";
import { toast } from "sonner";

const WhatsAppIcon = () => (
  <svg viewBox="0 0 32 32" className="h-6 w-6" aria-hidden="true">
    <path fill="#25D366" d="M16 .5A15.5 15.5 0 0 0 2.6 23.8L.5 31.5l7.9-2.1A15.5 15.5 0 1 0 16 .5Z" />
    <path fill="#fff" d="M16 3.3A12.7 12.7 0 0 0 5.1 22.5l.5.8-1.2 4.4 4.5-1.2.8.5A12.7 12.7 0 1 0 16 3.3Zm7.4 17.9c-.3.9-1.7 1.7-2.4 1.8-.6.1-1.3.2-2.1-.1-.5-.2-1.2-.4-2.1-.8-3.7-1.6-6.1-5.4-6.3-5.6-.2-.2-1.5-2-1.5-3.8s.9-2.7 1.3-3.1c.3-.3.7-.4 1-.4h.7c.2 0 .5 0 .8.6l1.1 2.7c.1.2.1.5 0 .7l-.5.7-.7.8c-.2.2-.4.5-.2.8.2.4.9 1.5 2 2.4 1.3 1.2 2.4 1.5 2.8 1.7.3.1.6.1.8-.1l1.2-1.4c.3-.4.6-.4 1-.3l2.7 1.3c.4.2.6.3.7.5.1.3.1 1.3-.2 2.2Z" />
  </svg>
);

const methods = [
  { id: "email", label: "Email", icon: <Mail className="h-5 w-5" /> },
  { id: "google", label: "Google", icon: <svg viewBox="0 0 48 48" className="h-6 w-6" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5Z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6C44.4 38.03 46.98 31.67 46.98 24.55Z" />
    <path fill="#FBBC05" d="M10.53 28.59A14.4 14.4 0 0 1 9.75 24c0-1.6.27-3.15.77-4.59l-7.97-6.2A23.9 23.9 0 0 0 0 24c0 3.87.93 7.51 2.56 10.79l7.97-6.2Z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.9-5.8l-7.73-6C30.03 37.64 27.29 38.5 24 38.5c-6.26 0-11.57-4.22-13.47-9.91l-7.97 6.2C6.51 42.62 14.62 48 24 48Z" />
  </svg> },
  { id: "facebook", label: "Facebook", icon: <svg viewBox="0 0 36 36" className="h-6 w-6" aria-hidden="true">
    <path fill="#1877F2" d="M36 18C36 8.06 27.94 0 18 0S0 8.06 0 18c0 8.98 6.58 16.43 15.19 17.78V23.2h-4.57V18h4.57v-3.97c0-4.51 2.69-7 6.8-7 1.97 0 4.03.35 4.03.35v4.43h-2.27c-2.24 0-2.94 1.39-2.94 2.82V18h5l-.8 5.2h-4.2v12.58C29.42 34.43 36 26.98 36 18Z" />
    <path fill="#fff" d="m25.01 23.2.8-5.2h-5v-3.37c0-1.43.7-2.82 2.94-2.82h2.27V7.38s-2.06-.35-4.03-.35c-4.11 0-6.8 2.49-6.8 7V18h-4.57v5.2h4.57v12.58a18.2 18.2 0 0 0 5.62 0V23.2h4.2Z" />
  </svg> },
  { id: "whatsapp", label: "WhatsApp", icon: <WhatsAppIcon /> },
];

export default function Register() {
  const {
    register, loginWithGoogle, loginWithFacebook,
    requestWhatsAppSignupOtp, verifyWhatsAppSignupOtp,
  } = useAuth();
  const navigate = useNavigate();
  const [method, setMethod] = useState("email");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    api.get("/homepage/settings")
      .then(({ data }) => setSettings(data || {}))
      .catch(() => setSettings({}));
  }, []);

  const media = Array.isArray(settings.login_images)
    ? settings.login_images.find((item) => item?.active !== false && (item?.image || item?.url))
    : null;
  const image = media?.image || media?.url || settings.login_image || "";
  const imageUrl = !image ? "" :
    /^(https?:|data:|blob:)/.test(image) || image.startsWith("/assets/") || image.startsWith("/nityavastra-")
      ? image
      : `${(API || "").replace(/\/api\/?$/, "")}${image.startsWith("/") ? "" : "/"}${image}`;

  const changeMethod = (next) => {
    setMethod(next);
    setOtpSent(false);
    setOtp("");
  };

  const submit = async (event) => {
    event.preventDefault();
    if (form.password.length < 6) {
      toast.error("Password must have at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await register(form.name.trim(), form.email.trim(), form.password);
      toast.success("Account created");
      navigate("/", { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const socialSignup = async (provider) => {
    setLoading(true);
    try {
      if (provider === "google") await loginWithGoogle();
      else await loginWithFacebook();
    } catch (error) {
      toast.error(error?.message || `Unable to continue with ${provider}`);
      setLoading(false);
    }
  };

  const requestCode = async () => {
    if (!form.name.trim() || !form.email.trim() || phone.length !== 10) {
      toast.error("Enter your name, email and 10-digit WhatsApp number");
      return;
    }
    setLoading(true);
    try {
      await requestWhatsAppSignupOtp(form.name.trim(), form.email.trim(), phone);
      setOtpSent(true);
      toast.success("Verification code sent through WhatsApp");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not send verification code");
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    if (!/^\d{6}$/.test(otp)) {
      toast.error("Enter the 6-digit verification code");
      return;
    }
    setLoading(true);
    try {
      await verifyWhatsAppSignupOtp(form.name.trim(), form.email.trim(), phone, otp);
      toast.success("Account created");
      navigate("/", { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not verify the code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="relative min-h-[80vh] overflow-hidden bg-[#FBF5EA] px-3 py-5 sm:px-5 lg:px-10 lg:py-12">
      <div className="pointer-events-none absolute inset-0 opacity-[0.035]"
        style={{ backgroundImage: "radial-gradient(circle at 1px 1px,#5B1725 1px,transparent 0)", backgroundSize: "22px 22px" }} />
      <div className="pointer-events-none absolute -left-32 top-16 h-96 w-96 rounded-full bg-[#B8871E]/15 blur-3xl" />
      <div className="pointer-events-none absolute -right-32 bottom-10 h-96 w-96 rounded-full bg-[#7C1F30]/10 blur-3xl" />
      <div className="relative mx-auto grid max-w-[1280px] overflow-hidden rounded-[26px] border border-[#C9A66B]/60 bg-[#FFFDF8] shadow-[0_22px_70px_rgba(73,43,24,0.16)] lg:grid-cols-[1.05fr_0.95fr]">
        <section className="relative min-h-[220px] overflow-hidden bg-[linear-gradient(135deg,#F3B73E,#D14D6C,#007C83)] sm:min-h-[280px] lg:min-h-[690px]">
          {imageUrl && <img src={imageUrl} alt="Nityavastra celebration collection" className="absolute inset-0 h-full w-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-t from-[#32111C]/85 via-[#32111C]/15 to-transparent" />
          <div className="absolute bottom-6 left-6 right-6 text-white sm:bottom-10 sm:left-10">
            <p className="text-[10px] font-semibold uppercase tracking-[0.26em] text-[#FFE59A]">Nityavastra · Heritage of India</p>
            <h1 className="mt-3 max-w-md font-serif text-3xl leading-tight sm:text-4xl lg:text-5xl">Your story starts here.</h1>
            <p className="mt-3 max-w-md text-sm leading-6 text-white/90">Discover beautiful Indian textiles, everyday comfort and pieces made for celebration.</p>
          </div>
        </section>

        <section className="flex items-center px-5 py-8 sm:px-10 sm:py-10 lg:px-12">
          <div className="mx-auto w-full max-w-md">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#B8871E]">Join Nityavastra</p>
            <h2 className="mt-2 font-serif text-3xl text-[#351A13] sm:text-4xl">Create your account</h2>
            <p className="mt-2 text-sm text-[#766154]">Choose how you would like to join.</p>

            <div className="mt-6 flex items-center justify-center gap-3" role="group" aria-label="Choose signup method">
              {methods.map((item) => (
                <button key={item.id} type="button" title={item.label}
                  aria-label={`Sign up with ${item.label}`}
                  aria-pressed={method === item.id}
                  onClick={() => changeMethod(item.id)}
                  className={`flex h-12 w-12 items-center justify-center rounded-full border-2 bg-white shadow-sm transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7C1F30] ${
                    method === item.id ? "border-[#7C1F30] ring-4 ring-[#7C1F30]/10" : "border-[#E1D2C2] hover:border-[#B8871E]"
                  }`}
                >{item.icon}</button>
              ))}
            </div>
            <p className="mt-2 text-center text-xs text-[#766154]">{methods.find((item) => item.id === method)?.label} signup</p>

            {method === "email" && (
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div><Label htmlFor="register-name">Full name</Label>
                  <div className="relative mt-1.5"><UserRound className="absolute left-4 top-3.5 h-4 w-4 text-[#A18C7B]" />
                    <Input id="register-name" required autoComplete="name" value={form.name}
                      onChange={(event) => setForm({ ...form, name: event.target.value })}
                      data-testid="register-name" className="h-12 rounded-xl border-[#D8C4A8] bg-white pl-11" />
                  </div>
                </div>
                <div><Label htmlFor="register-email">Email address</Label>
                  <div className="relative mt-1.5"><Mail className="absolute left-4 top-3.5 h-4 w-4 text-[#A18C7B]" />
                    <Input id="register-email" type="email" required autoComplete="email" value={form.email}
                      onChange={(event) => setForm({ ...form, email: event.target.value })}
                      data-testid="register-email" className="h-12 rounded-xl border-[#D8C4A8] bg-white pl-11" />
                  </div>
                </div>
                <div><Label htmlFor="register-password">Password</Label>
                  <div className="relative mt-1.5"><LockKeyhole className="absolute left-4 top-3.5 h-4 w-4 text-[#A18C7B]" />
                    <Input id="register-password" type={showPassword ? "text" : "password"} required minLength={6}
                      autoComplete="new-password" value={form.password}
                      onChange={(event) => setForm({ ...form, password: event.target.value })}
                      data-testid="register-password" className="h-12 rounded-xl border-[#D8C4A8] bg-white pl-11 pr-12" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      className="absolute right-4 top-3.5 text-[#766154]">{showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                  </div>
                </div>
                <Button type="submit" disabled={loading} data-testid="register-submit"
                  className="h-12 w-full rounded-xl bg-[#7C1F30] text-white hover:bg-[#8D2A3D]">
                  {loading ? "Creating account..." : "Create account"}
                </Button>
              </form>
            )}

            {(method === "google" || method === "facebook") && (
              <div className="mt-8 rounded-2xl border border-[#E8DDCF] bg-white p-5 text-center">
                <p className="text-sm leading-6 text-[#766154]">Continue securely with your {method === "google" ? "Google" : "Facebook"} account.</p>
                <Button type="button" disabled={loading} onClick={() => socialSignup(method)}
                  className={`mt-5 h-12 w-full rounded-xl text-white ${method === "google" ? "bg-[#7C1F30] hover:bg-[#8D2A3D]" : "bg-[#1877F2] hover:bg-[#1669D6]"}`}>
                  {loading ? "Connecting..." : `Continue with ${method === "google" ? "Google" : "Facebook"}`}
                </Button>
              </div>
            )}

            {method === "whatsapp" && (
              <div className="mt-6 space-y-4">
                <div><Label htmlFor="signup-whatsapp-name">Full name</Label>
                  <Input id="signup-whatsapp-name" value={form.name} disabled={otpSent}
                    onChange={(event) => setForm({ ...form, name: event.target.value })}
                    autoComplete="name" className="mt-1.5 h-12 rounded-xl border-[#D8C4A8] bg-white" />
                </div>
                <div><Label htmlFor="signup-whatsapp-email">Email address</Label>
                  <Input id="signup-whatsapp-email" type="email" value={form.email} disabled={otpSent}
                    onChange={(event) => setForm({ ...form, email: event.target.value })}
                    autoComplete="email" className="mt-1.5 h-12 rounded-xl border-[#D8C4A8] bg-white" />
                </div>
                <div>
                  <Label htmlFor="signup-whatsapp-phone">WhatsApp mobile number</Label>
                  <label htmlFor="signup-whatsapp-phone" className="mt-2 flex cursor-text items-center gap-1 sm:gap-1.5">
                    {Array.from({ length: 10 }, (_, index) => (
                      <span key={index} aria-hidden="true"
                        className={`flex h-11 min-w-0 flex-1 items-center justify-center rounded-xl border bg-white text-base font-semibold text-[#351A13] ${
                          phone.length === index && !otpSent ? "border-[#7C1F30]" : "border-[#D8C4A8]"
                        }`}>{phone[index] || ""}</span>
                    ))}
                  </label>
                  <input id="signup-whatsapp-phone" type="tel" inputMode="numeric" autoComplete="tel-national"
                    maxLength={10} value={phone} disabled={loading || otpSent}
                    onChange={(event) => setPhone(event.target.value.replace(/\D/g, "").slice(0, 10))}
                    className="sr-only"
                    aria-label="Enter 10-digit WhatsApp mobile number" placeholder="Type your 10-digit number"
                    data-testid="register-whatsapp-phone" />
                </div>
                {otpSent ? (
                  <>
                    <div><Label htmlFor="signup-whatsapp-otp">Verification code</Label>
                      <Input id="signup-whatsapp-otp" inputMode="numeric" maxLength={6} value={otp}
                        onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder="6-digit code" className="mt-1.5 h-12 rounded-xl border-[#D8C4A8] bg-white"
                        data-testid="register-whatsapp-otp" />
                    </div>
                    <Button type="button" disabled={loading || otp.length !== 6} onClick={verifyCode}
                      className="h-12 w-full rounded-xl bg-[#7C1F30] hover:bg-[#8D2A3D]">
                      {loading ? "Verifying..." : "Verify and create account"}
                    </Button>
                    <button type="button" onClick={() => { setOtpSent(false); setOtp(""); }}
                      className="w-full text-center text-xs text-[#7C1F30] hover:underline">Change details or request a new code</button>
                  </>
                ) : (
                  <Button type="button" disabled={loading || phone.length !== 10} onClick={requestCode}
                    className="h-12 w-full rounded-xl bg-[#25A765] hover:bg-[#208C55]">
                    {loading ? "Sending..." : "Send WhatsApp code"}
                  </Button>
                )}
              </div>
            )}

            <p className="mt-7 text-center text-sm text-[#766154]">
              Already have an account?{" "}
              <Link to="/login" data-testid="link-login" className="font-semibold text-[#7C1F30] hover:underline">Sign in</Link>
            </p>
          </div>
        </section>
      </div>
    </main>
  );
}
